import { VaultInfo, VaultSyncState } from '../stores/state';

/**
 * Strongly-typed vault localStorage manager
 * Fully encapsulates localStorage key structure and operations
 * 
 * Internal storage structure (never exposed):
 * - vault.[id].name: base64-encoded vault name
 * - vault.[id].sync: "true" if sync enabled, absent if sync disabled
 * - vault.[id].sync-date: ISO timestamp of last successful sync
 * - vault.[id].blob: base64-encoded encrypted vault data
 * - vault.[id].server-last-modified: server's Last-Modified timestamp (used for If-Unmodified-Since)
 * - vault.[id].date: ISO timestamp of last local modification
 * - vault.[id].sync-id: server-assigned vault ID for sync operations
 */

// Storage key constants (private to this module)
const VAULT_PREFIX = 'vault.';
const KEY_NAME = 'name';
const KEY_SYNC = 'sync';
const KEY_SYNC_DATE = 'sync-date';
const KEY_BLOB = 'blob';
const KEY_LAST_MODIFIED = 'server-last-modified';
const KEY_DATE = 'date';
const KEY_SYNC_ID = 'sync-id';

/**
 * Stored vault metadata from localStorage
 */
export interface StoredVaultMeta {
  vaultId: string;
  name: string;
  isSyncEnabled: boolean;
  lastSyncDate?: string;
  serverLastModified?: string;
  modifiedAt?: string;
  syncId?: string; // Server-assigned vault ID for sync operations
}

/**
 * Vault data to be saved to localStorage
 */
export interface VaultDataToSave {
  info: VaultInfo;
  syncState: VaultSyncState;
  encryptedBlob: string; // base64-encoded encrypted data
}

/**
 * Build a storage key for a vault property
 */
function buildKey(vaultId: string, property: string): string {
  return `${VAULT_PREFIX}${vaultId}.${property}`;
}

/**
 * Update the modification timestamp for a vault
 */
function updateModifiedTimestamp(vaultId: string): void {
  window.localStorage.setItem(buildKey(vaultId, KEY_DATE), new Date().toISOString());
}

/**
 * Encode a string to base64 (UTF-16 safe)
 */
function encodeBase64(str: string): string {
  return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) => {
    return String.fromCharCode(parseInt(p1, 16));
  }));
}

/**
 * Decode base64 to string (UTF-16 safe)
 */
function decodeBase64(str: string): string {
  return decodeURIComponent(atob(str).split('').map(c => {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));
}

// ============================================================================
// PUBLIC API - High-level vault operations
// ============================================================================

/**
 * Save a complete vault to localStorage
 */
export function saveVault(data: VaultDataToSave): void {
  const { info, syncState, encryptedBlob } = data;
  
  window.localStorage.setItem(buildKey(info.id, KEY_NAME), encodeBase64(info.name));
  window.localStorage.setItem(buildKey(info.id, KEY_BLOB), encryptedBlob);
  
  if (syncState.serverLastModified) {
    window.localStorage.setItem(buildKey(info.id, KEY_LAST_MODIFIED), syncState.serverLastModified);
  }
  
  // Note: Does not update KEY_SYNC or KEY_SYNC_DATE - those are managed by sync operations
  updateModifiedTimestamp(info.id);
}

/**
 * Get encrypted vault blob from localStorage
 */
export function getVaultBlob(vaultId: string): string | null {
  return window.localStorage.getItem(buildKey(vaultId, KEY_BLOB));
}

/**
 * Rename a vault
 */
export function renameVault(vaultId: string, newName: string): void {
  window.localStorage.setItem(buildKey(vaultId, KEY_NAME), encodeBase64(newName));
  updateModifiedTimestamp(vaultId);
}

/**
 * Update sync metadata for a vault
 */
export function updateSyncMetadata(vaultId: string, syncState: VaultSyncState): void {
  if (syncState.serverLastModified) {
    window.localStorage.setItem(buildKey(vaultId, KEY_LAST_MODIFIED), syncState.serverLastModified);
  } else {
    window.localStorage.removeItem(buildKey(vaultId, KEY_LAST_MODIFIED));
  }
  
  updateModifiedTimestamp(vaultId);
}

/**
 * Record the server's Last-Modified date for a vault (used after successful sync)
 * Also updates the last sync date to now
 */
export function setServerLastModified(vaultId: string, lastModified: string): void {
  window.localStorage.setItem(buildKey(vaultId, KEY_LAST_MODIFIED), lastModified);
  // Update lastSyncDate to current time when server-last-modified is updated (successful sync)
  window.localStorage.setItem(buildKey(vaultId, KEY_SYNC_DATE), new Date().toISOString());
  updateModifiedTimestamp(vaultId);
}

/**
 * Enable or disable sync for a vault
 */
export function setVaultSyncEnabled(vaultId: string, enabled: boolean): void {
  if (enabled) {
    window.localStorage.setItem(buildKey(vaultId, KEY_SYNC), 'true');
  } else {
    window.localStorage.removeItem(buildKey(vaultId, KEY_SYNC));
  }
  updateModifiedTimestamp(vaultId);
}

/**
 * Delete a vault from localStorage
 */
export function deleteVault(vaultId: string): void {
  window.localStorage.removeItem(buildKey(vaultId, KEY_NAME));
  window.localStorage.removeItem(buildKey(vaultId, KEY_SYNC));
  window.localStorage.removeItem(buildKey(vaultId, KEY_SYNC_DATE));
  window.localStorage.removeItem(buildKey(vaultId, KEY_BLOB));
  window.localStorage.removeItem(buildKey(vaultId, KEY_LAST_MODIFIED));
  window.localStorage.removeItem(buildKey(vaultId, KEY_DATE));
  window.localStorage.removeItem(buildKey(vaultId, KEY_SYNC_ID));
  // Remove legacy etag key left over from before the serverLastModified migration.
  window.localStorage.removeItem(`${VAULT_PREFIX}${vaultId}.etag`);
}

/**
 * Store a downloaded vault from server
 */
export interface DownloadedVaultData {
  vaultId: string;
  name?: string; // Optional - only set when explicitly provided (e.g., from vault list API)
  encryptedBlob: string; // base64-encoded
  enableSync: boolean;
  lastModified?: string; // ISO date string from server's Last-Modified header
  syncId?: string; // Server-assigned ID - if not provided, uses vaultId (for server-downloaded vaults)
}

export function storeDownloadedVault(data: DownloadedVaultData): void {
  const { vaultId, name, encryptedBlob, enableSync, lastModified, syncId } = data;
  
  // Only update name if explicitly provided - sync operations should not modify names
  if (name !== undefined) {
    window.localStorage.setItem(buildKey(vaultId, KEY_NAME), encodeBase64(name));
  }
  window.localStorage.setItem(buildKey(vaultId, KEY_BLOB), encryptedBlob);
  
  if (lastModified) {
    window.localStorage.setItem(buildKey(vaultId, KEY_LAST_MODIFIED), lastModified);
  }
  
  // Store syncId - for server-downloaded vaults, syncId = vaultId
  const effectiveSyncId = syncId || vaultId;
  window.localStorage.setItem(buildKey(vaultId, KEY_SYNC_ID), encodeBase64(effectiveSyncId));
  
  if (enableSync) {
    window.localStorage.setItem(buildKey(vaultId, KEY_SYNC), 'true');
    // Store current timestamp as lastSyncDate
    window.localStorage.setItem(buildKey(vaultId, KEY_SYNC_DATE), new Date().toISOString());
  }
  
  // Always update local modification timestamp
  updateModifiedTimestamp(vaultId);
}

/**
 * List all vaults stored in localStorage
 */
export function listVaults(): StoredVaultMeta[] {
  const vaultMap = new Map<string, StoredVaultMeta>();

  // Collect stale legacy keys encountered during the scan so we can remove
  // them after iteration (mutating localStorage mid-iteration is unsafe).
  // 'etag' was the property name used before the serverLastModified migration.
  const legacyKeysToRemove: string[] = [];

  // Scan localStorage for vault keys
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (!key?.startsWith(VAULT_PREFIX)) continue;
    
    const parts = key.split('.');
    if (parts.length !== 3) continue;
    
    const vaultId = parts[1];
    const property = parts[2];
    
    // Initialize vault entry if not exists
    if (!vaultMap.has(vaultId)) {
      vaultMap.set(vaultId, {
        vaultId,
        name: 'Unnamed Vault',
        isSyncEnabled: false
      });
    }
    
    const vault = vaultMap.get(vaultId)!;
    const value = window.localStorage.getItem(key);
    if (!value) continue;
    
    // Parse property values
    switch (property) {
      case KEY_NAME:
        vault.name = decodeBase64(value);
        break;
      case KEY_SYNC:
        vault.isSyncEnabled = value === 'true';
        break;
      case KEY_SYNC_DATE:
        vault.lastSyncDate = value; // Already ISO string
        break;
      case KEY_LAST_MODIFIED:
        vault.serverLastModified = value; // Already ISO string
        break;
      case KEY_DATE:
        vault.modifiedAt = value; // Already ISO string
        break;
      case KEY_SYNC_ID:
        vault.syncId = decodeBase64(value); // Decode base64-encoded syncId
        break;
      case 'etag':
        // Legacy key from before the serverLastModified migration (Phase 5).
        // The value is no longer used for sync decisions; flag for cleanup.
        legacyKeysToRemove.push(key);
        break;
    }
  }

  // One-time cleanup: remove stale etag entries left over from before the
  // If-Unmodified-Since migration so they don't accumulate in localStorage.
  legacyKeysToRemove.forEach((k) => window.localStorage.removeItem(k));
  
  return Array.from(vaultMap.values());
}

/**
 * Get metadata for a specific vault
 */
export function getVaultMetadata(vaultId: string): StoredVaultMeta | null {
  const nameKey = buildKey(vaultId, KEY_NAME);
  const name = window.localStorage.getItem(nameKey);
  
  if (!name) return null;
  
  const syncValue = window.localStorage.getItem(buildKey(vaultId, KEY_SYNC));
  const syncDateValue = window.localStorage.getItem(buildKey(vaultId, KEY_SYNC_DATE));
  const lastModifiedValue = window.localStorage.getItem(buildKey(vaultId, KEY_LAST_MODIFIED));
  const dateValue = window.localStorage.getItem(buildKey(vaultId, KEY_DATE));
  const syncIdValue = window.localStorage.getItem(buildKey(vaultId, KEY_SYNC_ID));
  
  return {
    vaultId,
    name: decodeBase64(name),
    isSyncEnabled: syncValue === 'true',
    lastSyncDate: syncDateValue || undefined,
    serverLastModified: lastModifiedValue || undefined,
    modifiedAt: dateValue || undefined,
    syncId: syncIdValue ? decodeBase64(syncIdValue) : undefined
  };
}

/**
 * Set the syncId (server-assigned vault ID) for a vault
 */
export function setVaultSyncId(vaultId: string, syncId: string): void {
  window.localStorage.setItem(buildKey(vaultId, KEY_SYNC_ID), encodeBase64(syncId));
}

/**
 * Get the syncId for a vault (returns undefined if not registered with server)
 */
export function getVaultSyncId(vaultId: string): string | undefined {
  const value = window.localStorage.getItem(buildKey(vaultId, KEY_SYNC_ID));
  return value ? decodeBase64(value) : undefined;
}

/**
 * Check if a vault exists in localStorage
 */
export function vaultExists(vaultId: string): boolean {
  return window.localStorage.getItem(buildKey(vaultId, KEY_NAME)) !== null;
}
